/**
 * Text + Image Section
 * Split content block with text and image
 */

import React from 'react';
import { Link } from 'react-router-dom';
import useDarkModeNeutrals from '../../hooks/useDarkModeNeutrals';
import './sections.css';

function TextImage({ settings = {}, content = {}, globalStyles = {}, store }) {
  const neutrals = useDarkModeNeutrals();
  const {
    imagePosition = 'right', // left, right
    textAlign = 'left',
    backgroundColor = neutrals.background,
    // Granular style settings
    titleColor = neutrals.text,
    textColor = neutrals.textMuted,
    buttonBgColor,
    buttonTextColor = '#ffffff',
    buttonStyle = 'filled',
    imageRadius = '12px',
    contentPadding = 'medium',
  } = settings;

  const {
    title = '',
    subtitle = '',
    text = '',
    buttonText = '',
    buttonLink = '',
    image = '',
    imageAlt = '',
  } = content;

  const colors = globalStyles.colors || {};
  const fonts = globalStyles.fonts || {};

  const effectiveButtonBgColor = buttonBgColor || colors.primary || '#2563eb';

  const paddingMap = {
    small: '24px',
    medium: '48px',
    large: '80px',
  };

  const sectionStyle = {
    backgroundColor,
  };

  const titleStyleObj = {
    fontFamily: fonts.heading || 'inherit',
    color: titleColor,
    textAlign,
  };

  const subtitleStyleObj = {
    color: effectiveButtonBgColor,
  };

  const textStyleObj = {
    textAlign,
    color: textColor,
  };

  const imageStyle = {
    borderRadius: imageRadius,
  };

  const contentStyle = {
    padding: paddingMap[contentPadding] || paddingMap.medium,
  };

  // Build button styles based on buttonStyle setting
  const buttonStyleObj = {
    borderRadius: globalStyles.buttons?.borderRadius || '8px',
    padding: globalStyles.buttons?.padding || '12px 24px',
  };

  if (buttonStyle === 'outline') {
    buttonStyleObj.backgroundColor = 'transparent';
    buttonStyleObj.border = `2px solid ${effectiveButtonBgColor}`;
    buttonStyleObj.color = effectiveButtonBgColor;
  } else if (buttonStyle === 'ghost') {
    buttonStyleObj.backgroundColor = 'transparent';
    buttonStyleObj.color = effectiveButtonBgColor;
  } else {
    buttonStyleObj.backgroundColor = effectiveButtonBgColor;
    buttonStyleObj.color = buttonTextColor;
  }

  return (
    <section className="ts-text-image" style={sectionStyle}>
      <div className={`ts-text-image__container ts-text-image__container--${imagePosition}`}>
        <div className="ts-text-image__content" style={contentStyle}>
          {subtitle && (
            <span className="ts-text-image__subtitle" style={subtitleStyleObj}>
              {subtitle}
            </span>
          )}
          {title && (
            <h2 className="ts-text-image__title" style={titleStyleObj}>
              {title}
            </h2>
          )}
          {text && (
            <div className="ts-text-image__text" style={textStyleObj}>
              {text.split('\n').map((paragraph, i) => (
                <p key={i}>{paragraph}</p>
              ))}
            </div>
          )}
          {buttonText && buttonLink && (
            <Link
              to={buttonLink}
              className="ts-text-image__button"
              style={buttonStyleObj}
            >
              {buttonText}
            </Link>
          )}
        </div>

        <div className="ts-text-image__image-wrapper">
          {image ? (
            <img
              src={image}
              alt={imageAlt || title || 'Section image'}
              className="ts-text-image__image"
              style={imageStyle}
              loading="lazy"
            />
          ) : (
            <div className="ts-text-image__placeholder" style={imageStyle}>
              <svg width="64" height="64" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                <rect x="3" y="3" width="18" height="18" rx="2" />
                <circle cx="8.5" cy="8.5" r="1.5" />
                <path d="M21 15l-5-5L5 21" />
              </svg>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

export default React.memo(TextImage);
